/* eslint-disable @typescript-eslint/no-misused-promises */
import React, { useEffect, useMemo, useState } from 'react'
import { Box, Stack, Switch, Typography } from '@mui/material'
import { useForm, FormProvider, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useAppDispatch } from '../../../../store'
import FormInput from '../../../../components/FormElements/FormInput'
import DefaultModalActions from '../../../../components/GenericModal/DefaultModalActions'
import { getTermsSectionsApi, getEnrollmentsApi } from '../../../../store/slices/termsSectionsSlice/api'
import { grey } from '../../../../colors'
import { type IUser } from '../../../../models/User'
import { type IAttribute, type ISection } from '../../../../models/TermsSection'
import { debounce, isEmpty } from 'lodash'
import api from '../../../../utils/api'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import Tab from '@mui/material/Tab'
import { CrossListCodeSectionDisplay } from './components/CrossListCodeSectionsDisplay'
import { getDirtyValues } from '../../../../utils/onlyUpdatedFields'
import { notification } from '../../../../components/Notifications'
import ValidationAlert from '../../../../components/ValidationAlert'
import { bindSetError, showServerFieldErrors } from '../../../../utils/showServerFieldErrors'
import { StudentsAndEnrollment } from '../../../StudentsAndEnrollmentFeature'
import { ProjectTabPanel } from './components/ProjectTabPanel'
import { InitialForm } from './components/InitialForm'
import { LastUpdateMeta } from '../../../../components/GenericModal/ChangeMetaInfo'
import { useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { type ITermCDG } from '../../../../models/Term'
import { useAccess } from '../../../../hooks/useAccess'
import { READONLY_TOOLTIP } from '../../../../consts'
import { v4 as uuidv4 } from 'uuid'
import { useSlotBackgroundTask } from '../../../../hooks/useSlotBackgroundTask'
import { notifySlotTaskTerminal } from '../../../../utils/notifySlotTaskTerminal'
import { getEnqueuedTaskIds } from '../../../../utils/enqueuedTaskIds'

interface FormType {
  courseId: string
  abbreviation: string
  title: string
  isPrimary: boolean
  crossListCode?: string | null
  startTimestamp: Date
  endTimestamp: Date
  primaryInstructors?: IUser[]
  additionalInstructors?: IUser[]
  teachingAssistants?: IUser[]
  attributes?: IAttribute[]
}

interface CreateAndEditTermsSectionProps {
  selectedRow?: ISection | Record<string, any>
  selectedRowWithActions?: any
  handleCloseModal?: () => void
  params: Record<string, any>
  isTaskRunning?: boolean
  onTaskBlock?: () => void
}

export const CreateAndEditTermsSection: React.FC<CreateAndEditTermsSectionProps> = ({
  selectedRow,
  selectedRowWithActions,
  handleCloseModal = () => { },
  params,
  isTaskRunning,
  onTaskBlock
}) => {
  const { readonly } = useAccess(selectedRowWithActions?.actions ?? [])
  const [errors, setErrors] = useState<unknown>()

  useEffect(() => {
    if (selectedRow != null && isTaskRunning) {
      onTaskBlock?.()
      handleCloseModal()
    }
  }, [selectedRow])
  const [tab, setTab] = useState<string>('1')
  const [loading, setLoading] = useState<boolean>(false)
  const dispatch = useAppDispatch()
  const queryClient = useQueryClient()
  const { id: urlTermId } = useParams()
  const termsDetails = queryClient.getQueryData(['getTermById', urlTermId]) as ITermCDG

  const isEditAction = Boolean(selectedRow)
  const { isPrimary, abbreviation, title, course, startTimestamp, endTimestamp, primaryInstructors, additionalInstructors, teachingAssistants, attributes } = selectedRow ?? {}
  const [crossListCode, setCrossListCode] = useState<string>(selectedRow?.crossListCode)
  const { abbreviation: courseAbbreviation } = course ?? {}
  const [newlyEnrolled, setNewlyEnrolled] = useState<IUser[]>([])
  const [newlyUnenrolled, setNewlyUnenrolled] = useState<IUser[]>([])
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
  const isSurveyProjectsTab = isEditAction && tab === '4'

  // Instructor-added-question seeding isn't scoped to one Distribution Plan/slot, so there's
  // no real slotId to key on here. A section id is stable across edits; creates don't have one
  // yet, so use a random per-modal-instance key instead — its only job is a distinct
  // localStorage bucket for this submission, not identifying which section it is.
  const [taskTrackingId] = useState<string>(() => selectedRow?.sectionId ? `section-${selectedRow.sectionId}` : `section-create-${uuidv4()}`)
  const { setTask } = useSlotBackgroundTask({ slotId: taskTrackingId, onTerminal: notifySlotTaskTerminal })

  const termFormSchema = yup.object().shape({
    courseId: yup.string().required('Course Id is required'),
    abbreviation: yup.string()
      .required('Section Id is required')
      .test('section-not-equal-course', 'Section ID cannot be the same as Course ID. Try adding a suffix like "-01" to make it unique.', function (value) {
        const courseId = (this.parent && typeof this.parent.courseId === 'string') ? this.parent.courseId : ''
        const sectionId = value.replace(/\s+/g, '').toLowerCase()
        const courseIdNormalized = courseId.replace(/\s+/g, '').toLowerCase()
        return sectionId !== courseIdNormalized
      }),
    title: yup.string().required('Title is required'),
    crossListCode: yup.string().nullable(),
    isPrimary: yup.boolean().required(),
    startTimestamp: yup.date()
      .required('Start Date is required')
      .max(yup.ref('endTimestamp'), 'Start Date must be before End Date'),
    endTimestamp: yup.date()
      .required('End Date is required')
      .min(yup.ref('startTimestamp'), 'End Date must be after Start Date')
      .test('is-before-term-end', 'End Date must be before term end date', function (value) {
        const { endTimestamp } = termsDetails?.data ?? {}
        return !endTimestamp || value <= new Date(endTimestamp)
      })
  })

  const selectedInstructors = primaryInstructors?.map((user: { id: string, title: string }) => ({
    userId: user.id,
    name: user.title
  }))

  const selectedAdditionalInstructors = additionalInstructors?.map((user: { id: string, title: string }) => ({
    userId: user.id,
    name: user.title
  }))

  const selectedTeachingAssistants = teachingAssistants?.map((user: { id: string, title: string }) => ({
    userId: user.id,
    name: user.title
  }))

  const methods = useForm<FormType>({
    resolver: yupResolver(termFormSchema),
    defaultValues: {
      courseId: courseAbbreviation,
      abbreviation,
      title,
      startTimestamp: startTimestamp ?? termsDetails?.data?.startTimestamp,
      endTimestamp: endTimestamp ?? termsDetails?.data?.endTimestamp,
      primaryInstructors: selectedInstructors,
      additionalInstructors: selectedAdditionalInstructors,
      teachingAssistants: selectedTeachingAssistants,
      attributes,
      isPrimary: isPrimary ?? false,
      crossListCode: selectedRow?.crossListCode
    }
  })

  const { handleSubmit, control, formState: { dirtyFields }, trigger, watch } = methods

  React.useEffect(() => {
    const subscription = watch(async (values, { name }) => {
      if (name === 'abbreviation' || name === 'courseId') {
        await trigger('abbreviation')
      }
    })
    return () => { subscription.unsubscribe() }
  }, [watch, trigger])

  const handleUpdateEnrollments = (): void => {
    setIsConfirmModalOpen(true)
  }

  const refreshAllCDGs = async (): Promise<void> => {
    await dispatch(getTermsSectionsApi({ params }))
    if (termsDetails?.data?.termId) {
      try {
        await dispatch(getEnrollmentsApi({ urlId: termsDetails.data.termId, params: { pageSize: 15 } }))
      } catch (error) {
        console.error('Failed to refresh Student Enrollment CDG:', error)
      }
    }
  }

  const onSubmit = async (data: FormType): Promise<void> => {
    if (isEmpty(data?.crossListCode)) {
      data.crossListCode = null
    }
    try {
      setLoading(true)
      const changedFields = getDirtyValues(dirtyFields, data)
      let res
      if (isEditAction) {
        res = await api.patch(`/api/sections/${selectedRow?.sectionId}`, changedFields)
        notification(`${res.data?.abbreviation} was updated successfully.`, { variant: 'success', subTitle: res.data?.abbreviation })
      } else {
        res = await api.post(`/api/sections/?term_id=${params?.termId}`, data)
        notification(`${res.data?.abbreviation} was created successfully.`, { variant: 'success', subTitle: res.data?.abbreviation })
      }

      // Every id, not just the first: assigning an instructor prepares their default questions
      // once per Draft or Published Survey Project the section belongs to, so a section in three
      // such projects answers with three tasks (Backend `add_roles_to_section`). The first
      // finishing says nothing about the rest.
      const seedTaskIds = getEnqueuedTaskIds(res)
      if (seedTaskIds.length > 0) {
        setTask(
          seedTaskIds,
          res.data?.message ?? 'Instructor questions are being prepared in the background.',
          'Instructor questions are ready.'
        )
      }

      handleCloseModal()
      await refreshAllCDGs()
    } catch (error) {
      setErrors(showServerFieldErrors(error, bindSetError(methods.setError), methods.clearErrors))
    } finally {
      setLoading(false)
    }
  }

  const onInputChange = (value: string): void => {
    setCrossListCode(value)
  }

  const getDebouncedInput = useMemo(
    () => debounce(onInputChange, 500),
    [onInputChange]
  )

  return (
    <FormProvider {...methods}>
      <ValidationAlert errors={errors} />
      <form onSubmit={
        newlyEnrolled.length + newlyUnenrolled.length === 0
          ? handleSubmit(onSubmit)
          : handleSubmit(handleUpdateEnrollments)
      }>
        <TabContext value={tab}>
          <TabList onChange={(e, v: string) => { setTab(v) }} sx={{ paddingBottom: 1, position: 'sticky', top: 0, zIndex: 1, backgroundColor: grey[0] }}>
            <Tab label="Details" value="1" />
            <Tab label="Cross-Listing" value="2" />
            {isEditAction && <Tab label="Enrollments" value="3" />}
            {isEditAction && <Tab label="Survey Projects" value="4" />}
          </TabList>

          <TabPanel value="1" sx={{ paddingTop: 4 }}>
            <InitialForm
              isEditAction={isEditAction}
              selectedRow={selectedRow}
              control={control}
              readonly={readonly}
            />
          </TabPanel>
          <TabPanel value="2" sx={{ paddingTop: 4 }}>
            <Stack spacing={3}>
              <Box sx={{ pb: 2 }}>
                <Box display="flex" alignItems="center" gap={2}>
                  <Controller
                    name="isPrimary"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <Switch checked={value} onChange={onChange} />
                    )}
                  />
                  <Typography variant="p5" sx={{ fontWeight: 'bold' }}>Is Primary Course</Typography>
                </Box>
                <Typography variant="p5">Indicates that this Course is the primary sponsor of the course material</Typography>
              </Box>
              <FormInput name="crossListCode" label="Cross-List Code" control={control} onChange={(e) => { getDebouncedInput(e?.target?.value) }} />
              <CrossListCodeSectionDisplay crossListCode={crossListCode} termId={termsDetails?.data?.termId} />
            </Stack>
          </TabPanel>
          {isEditAction && (
            <TabPanel value="3" sx={{ paddingTop: 4 }}>
              <StudentsAndEnrollment
                newlyEnrolled={newlyEnrolled}
                setNewlyEnrolled={setNewlyEnrolled}
                newlyUnenrolled={newlyUnenrolled}
                setNewlyUnenrolled={setNewlyUnenrolled}
                sectionId={selectedRow?.sectionId ?? ''}
                termId={termsDetails?.data?.termId ?? ''}
                abbreviation={abbreviation ?? ''}
                isConfirmModalOpen={isConfirmModalOpen}
                setIsConfirmModalOpen={setIsConfirmModalOpen}
                handleSubmit={async () => { await refreshAllCDGs() }}
                onAllClose={handleCloseModal}
              />
            </TabPanel>
          )}

          {isEditAction && (
            <TabPanel value="4" sx={{ paddingTop: 4 }}>
              <ProjectTabPanel
                selectedRow={selectedRow}
                termId={termsDetails?.data?.termId ?? ''}
                readonly={readonly}
                onRefresh={refreshAllCDGs}
                onParentClose={handleCloseModal}
              />
            </TabPanel>
          )}
        </TabContext>
        <DefaultModalActions
          destructiveButtonProps={{
            onClick: handleCloseModal
          }}
          confirmButtonProps={{
            text: isEditAction ? 'Save' : 'Create',
            loading,
            type: 'submit',
            disabled: readonly || isSurveyProjectsTab,
            hideButton: isSurveyProjectsTab,
            tooltip: readonly ? READONLY_TOOLTIP : undefined
          }}
          lastUpdateMeta={isEditAction && (
            <LastUpdateMeta lastEditedBy={selectedRow?.modifiedByUsername} lastEditedAt={selectedRow?.modifiedOn} />
          )}

        />
      </form>
    </FormProvider>
  )
}
